import React from 'react';
import { Sparkles, GraduationCap, Users, Palette, Globe2, ArrowRight, Instagram, Play, ClipboardEdit, MessageCircle, Brain, BookOpen, Cpu, Languages, PiggyBank, Music, Earth, CheckCircle2, CalendarDays, Award, Star, CreditCard } from 'lucide-react';
import { createStripeCheckout } from '../utils/stripe';
import { Button } from './ui/button';
import { Card } from './ui/card';

const PILLARS = [
  {
    icon: Sparkles,
    title: 'Spiritually Grounded',
    description:
      'Rooted in faith, values, and purpose — children who know who they are and Whose they are.',
    accent: 'from-amber-400 to-orange-500',
    iconBg: 'bg-amber-100',
    iconColor: 'text-amber-600',
    testId: 'pillar-spiritual',
  },
  {
    icon: GraduationCap,
    title: 'Academically Sound',
    description:
      'Equipped with knowledge, critical thinking, and a lifelong love of learning that opens every door.',
    accent: 'from-indigo-500 to-blue-600',
    iconBg: 'bg-indigo-100',
    iconColor: 'text-indigo-600',
    testId: 'pillar-academic',
  },
  {
    icon: Users,
    title: 'Socially Relevant',
    description:
      'Confident, compassionate, and culturally aware — able to engage their world and build healthy relationships.',
    accent: 'from-emerald-500 to-teal-600',
    iconBg: 'bg-emerald-100',
    iconColor: 'text-emerald-600',
    testId: 'pillar-social',
  },
  {
    icon: Palette,
    title: 'Creative',
    description:
      'Imaginative, expressive, and innovative — children who create rather than only consume.',
    accent: 'from-pink-500 to-rose-600',
    iconBg: 'bg-pink-100',
    iconColor: 'text-pink-600',
    testId: 'pillar-creative',
  },
];

const INSTAGRAM_URL = 'https://www.instagram.com/global360child?igsh=ZzIzbWN3ZjN2N3Z0&utm_source=qr';
const YOUTUBE_REPLAY_URL = 'https://youtu.be/H8f8v83qTyU?si=VQHv7D8JYuAs98zr';
const REGISTRATION_URL =
  'https://docs.google.com/forms/d/e/1FAIpQLSdkKC2-I1xejE34mJM4al1bX9xntfomoSQBPr3xSZoKc1xisA/viewform?usp=sharing';
const WHATSAPP_NUMBER_DISPLAY = '+49 1521 0811916';
const WHATSAPP_URL = 'https://wa.me/4915210811916?text=Hi%21%20I%27m%20interested%20in%20the%20Global%20360%C2%B0%20Child%20bootcamp.';
const WHATSAPP_LEARNING_CLUB_URL = 'https://wa.me/4915210811916?text=Hi%21%20I%27d%20like%20to%20enroll%20my%20child%20in%20the%20Global%20360%C2%B0%20Child%20Monthly%20Learning%20Club%20%28%E2%82%A650%2C000%20%2F%20%2450%20%2F%20%E2%82%AC50%20per%20child%20per%20month%29.';
const WHATSAPP_MENTORSHIP_URL = 'https://wa.me/4915210811916?text=Hi%21%20I%27d%20like%20more%20information%20about%20the%20Global%20360%C2%B0%20Child%20U.S.%20College%20Admissions%20%26%20Scholarship%20Mentorship%20program%20%28%243%2C000%29.';

const LEARNING_CLUB_SECTIONS = [
  {
    icon: Brain,
    title: 'Personal Growth',
    subtitle: 'Character & confidence',
    items: ['Emotional Intelligence', 'Confidence Building', 'Public Speaking', 'Critical Thinking', 'Problem Solving', 'Goal Setting'],
    flyer: 'https://customer-assets-gfyr7b9c.emergentagent.net/job_leadership-impact-3/artifacts/r87u1ob1_e74738fe-bdf7-45e2-ab50-5056be5f84f1.jpeg',
    accent: 'from-amber-400 to-orange-500',
  },
  {
    icon: BookOpen,
    title: 'Faith & Character',
    subtitle: 'Spiritual & moral development',
    items: ['Bible Study', 'Character Development', 'Identity & Purpose', 'Leadership', 'Social Skills', 'Values & Integrity'],
    flyer: 'https://customer-assets-gfyr7b9c.emergentagent.net/job_leadership-impact-3/artifacts/7y431n75_4132ada3-3e5d-482b-96e0-489acfc8da1e.jpeg',
    accent: 'from-purple-500 to-indigo-600',
  },
  {
    icon: Cpu,
    title: 'Technology & Innovation',
    subtitle: 'Future-ready digital skills',
    items: ['Computer Fundamentals', 'Website Design (HTML/CSS/JS)', 'Python Programming', 'Data Science', 'AI for Beginners', 'Cyber Safety', 'Digital Creativity'],
    flyer: 'https://customer-assets-gfyr7b9c.emergentagent.net/job_leadership-impact-3/artifacts/qjtuc4wl_30713c74-1853-49cd-ac70-7700e42da73e.jpeg',
    accent: 'from-blue-500 to-cyan-600',
  },
  {
    icon: Languages,
    title: 'Languages & Culture',
    subtitle: 'Multilingualism & appreciation',
    items: ['Yoruba', 'Igbo', 'Hausa', 'French', 'Spanish', 'German', 'Portuguese'],
    flyer: 'https://customer-assets-gfyr7b9c.emergentagent.net/job_leadership-impact-3/artifacts/7forsciz_6035e3ea-466f-469b-94cd-235f901dab3a.jpeg',
    accent: 'from-rose-500 to-red-500',
  },
  {
    icon: PiggyBank,
    title: 'Financial Literacy & Entrepreneurship',
    subtitle: 'Money & business skills',
    items: ['Saving & Budgeting', 'Entrepreneurship', 'Business Ideas', 'Digital Entrepreneurship', 'Value Creation'],
    flyer: 'https://customer-assets-gfyr7b9c.emergentagent.net/job_leadership-impact-3/artifacts/b2x8forg_8a53777f-04d3-4fa8-8d44-51101118a19e.jpeg',
    accent: 'from-emerald-500 to-green-600',
  },
  {
    icon: Music,
    title: 'Creativity & Expression',
    subtitle: 'Discover & express talents',
    items: ['Music', 'Creative Writing', 'Art & Design', 'Storytelling', 'Drama', 'Content Creation'],
    flyer: 'https://customer-assets-gfyr7b9c.emergentagent.net/job_leadership-impact-3/artifacts/n6xrp1v2_b16f616b-31bc-494d-be55-319d9e163a66.jpeg',
    accent: 'from-pink-500 to-fuchsia-600',
  },
  {
    icon: Earth,
    title: 'Global Citizenship',
    subtitle: 'Make a positive impact',
    items: ['Climate Education', 'Community Service', 'Cultural Awareness', 'Healthy Living', 'Digital Citizenship'],
    flyer: 'https://customer-assets-gfyr7b9c.emergentagent.net/job_leadership-impact-3/artifacts/271vcxln_7aa22f9a-0ba1-40b6-afc4-e66f70877045.jpeg',
    accent: 'from-teal-500 to-cyan-600',
  },
];

const WEEKLY_SCHEDULE_FRIDAY = [
  { time: '4:00 – 5:00 PM WAT', focus: 'Faith & Character' },
  { time: '5:00 – 6:00 PM WAT', focus: 'Personal Growth' },
  { time: '6:00 – 7:00 PM WAT', focus: 'Languages & Culture' },
  { time: '7:00 – 8:00 PM WAT', focus: 'Technology & Innovation' },
  { time: '8:00 – 9:00 PM WAT', focus: 'Financial Literacy & Entrepreneurship' },
  { time: '9:00 – 10:00 PM WAT', focus: 'Creativity & Expression' },
  { time: '10:00 – 11:00 PM WAT', focus: 'Global Citizenship' },
];

const WEEKLY_SCHEDULE_SATURDAY = [
  { time: '8:00 – 10:00 AM WAT', focus: 'Faith & Character' },
  { time: '10:00 AM – 12:00 PM WAT', focus: 'Personal Growth' },
  { time: '12:00 – 2:00 PM WAT', focus: 'Languages & Culture' },
  { time: '2:00 – 4:00 PM WAT', focus: 'Technology & Innovation' },
  { time: '4:00 – 6:00 PM WAT', focus: 'Financial Literacy & Entrepreneurship' },
  { time: '6:00 – 8:00 PM WAT', focus: 'Creativity & Expression' },
  { time: '8:00 – 10:00 PM WAT', focus: 'Global Citizenship' },
];

const MEMBERSHIP_BENEFITS = [
  'Live weekly classes',
  'Access to class recordings',
  'Monthly projects',
  'Downloadable learning resources',
  'Activity worksheets',
  'Monthly challenges',
  'Guest speakers',
  'Certificates of participation',
  'Parent webinars',
  'Members-only community',
];
const BOOK_LAUNCH_POSTER =
  'https://customer-assets.emergentagent.com/job_leadership-impact-3/artifacts/4cnivc9o_99e0a9ed-2746-4ceb-9e06-d3b854ccb1bf.jpeg';
const GLOBAL_360_LOGO =
  'https://customer-assets.emergentagent.com/job_leadership-impact-3/artifacts/3anb2elq_ChatGPT_Image_May_26_2026_at_01_31_17_AM.png';

const PROGRAMS = [
  {
    title: 'Summer Bootcamp — Virtual Edition',
    tagline: 'Ages 6 – 18 · 6 Weeks · 2 Hrs Daily · Live on Zoom',
    poster:
      'https://customer-assets.emergentagent.com/job_leadership-impact-3/artifacts/0vhs522j_ChatGPT_Image_May_26_2026_at_01_32_20_AM.png',
    alt: 'Global 360° Child Summer Bootcamp — Virtual Edition flyer',
    accent: 'from-red-500 via-amber-400 to-emerald-500',
    badge: '50% OFF — Limited Time',
    description:
      'Bible & Character, Emotional Intelligence, Tech & Digital Skills, Communication, Financial Literacy, Music & Creativity, Leadership and more. Family discounts available.',
    testId: 'program-summer-bootcamp',
  },
  {
    title: 'Teen Web & Data Bootcamp',
    tagline: 'Ages 13 – 19 · Build · Analyze · Innovate',
    poster:
      'https://customer-assets.emergentagent.com/job_leadership-impact-3/artifacts/z215h3te_ChatGPT_Image_May_26_2026_at_07_36_06_PM.png',
    alt: 'Global 360° Child Teen Web & Data Bootcamp flyer',
    accent: 'from-blue-600 via-indigo-500 to-purple-600',
    badge: 'Highly Discounted',
    description:
      'Build websites from scratch (HTML / CSS / JavaScript), Python programming, real-world data analysis, portfolio projects, career & life skills, and a certificate of completion.',
    testId: 'program-teen-bootcamp',
  },
];

const Global360Child = ({ scrollToSection }) => {
  return (
    <section
      id="global-360-child"
      className="py-24 px-6 bg-gradient-to-br from-slate-900 via-indigo-900 to-purple-900 relative overflow-hidden"
      data-testid="global-360-child-section"
    >
      {/* Decorative globe rings */}
      <div className="absolute -top-32 -right-32 w-96 h-96 rounded-full border border-white/5"></div>
      <div className="absolute -top-20 -right-20 w-72 h-72 rounded-full border border-white/10"></div>
      <div className="absolute -bottom-40 -left-40 w-[28rem] h-[28rem] rounded-full border border-white/5"></div>

      <div className="max-w-7xl mx-auto relative z-10">
        {/* Header */}
        <div className="text-center mb-16">
          <div className="flex justify-center mb-6">
            <img
              src={GLOBAL_360_LOGO}
              alt="Global 360° Child logo"
              className="w-48 md:w-64 h-auto rounded-2xl bg-white/95 p-3 shadow-2xl"
              data-testid="global-360-logo"
            />
          </div>
          <div className="inline-flex items-center gap-2 px-4 py-2 mb-6 rounded-full bg-white/10 backdrop-blur-sm border border-white/20">
            <Globe2 className="h-4 w-4 text-amber-300" />
            <span className="text-sm font-medium text-amber-100 tracking-wide">An Initiative for Global Impact</span>
          </div>
          <h2 className="text-4xl md:text-6xl font-serif font-bold text-white mb-4">
            Global <span className="bg-gradient-to-r from-amber-300 via-pink-300 to-cyan-300 bg-clip-text text-transparent">360°</span> Child
          </h2>
          <div className="w-24 h-1 bg-gradient-to-r from-amber-400 via-pink-400 to-cyan-400 mx-auto mb-6"></div>
          <p className="text-lg md:text-xl text-indigo-100 max-w-3xl mx-auto leading-relaxed mb-3">
            Raising a generation of well-rounded children — <span className="text-white font-semibold">spiritually grounded, academically sound, socially relevant, and creative</span> — equipped to make a difference wherever life takes them.
          </p>
          <p className="text-sm md:text-base text-amber-200 font-medium tracking-wide">
            Nurture · Equip · Inspire · Impact
          </p>
        </div>

        {/* Pillars Grid */}
        <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6 mb-12">
          {PILLARS.map((pillar) => {
            const Icon = pillar.icon;
            return (
              <Card
                key={pillar.title}
                className="group relative p-7 bg-white/5 backdrop-blur-md border border-white/10 hover:bg-white/10 hover:-translate-y-2 transition-all duration-300 overflow-hidden"
                data-testid={pillar.testId}
              >
                <div className={`absolute top-0 left-0 right-0 h-1 bg-gradient-to-r ${pillar.accent}`}></div>
                <div className={`inline-flex items-center justify-center h-14 w-14 rounded-xl ${pillar.iconBg} mb-5 group-hover:scale-110 transition-transform`}>
                  <Icon className={`h-7 w-7 ${pillar.iconColor}`} />
                </div>
                <h3 className="text-xl font-bold text-white mb-3">{pillar.title}</h3>
                <p className="text-sm text-indigo-100 leading-relaxed">{pillar.description}</p>
              </Card>
            );
          })}
        </div>

        {/* Upcoming Programs */}
        <div className="mb-12">
          <div className="text-center mb-8">
            <div className="inline-block px-3 py-1 mb-3 rounded-full bg-emerald-400/20 border border-emerald-300/40 text-emerald-200 text-xs font-semibold tracking-wide uppercase">
              Registration Open
            </div>
            <h3 className="text-3xl md:text-4xl font-serif font-bold text-white mb-3">
              Upcoming Programs
            </h3>
            <p className="text-indigo-100 max-w-2xl mx-auto">
              Live virtual bootcamps designed to equip children &amp; teens with the skills, character, and confidence to lead in the digital age.
            </p>
          </div>

          <div className="grid md:grid-cols-2 gap-6 mb-8">
            {PROGRAMS.map((program) => (
              <a
                key={program.title}
                href={REGISTRATION_URL}
                target="_blank"
                rel="noopener noreferrer"
                className="group relative block rounded-2xl overflow-hidden shadow-2xl bg-white/5 backdrop-blur-md border border-white/15 hover:border-white/30 hover:-translate-y-1 transition-all duration-300"
                data-testid={program.testId}
              >
                <div className={`absolute top-0 left-0 right-0 h-1.5 bg-gradient-to-r ${program.accent} z-10`}></div>

                <div className="relative overflow-hidden">
                  <img
                    src={program.poster}
                    alt={program.alt}
                    className="w-full h-auto group-hover:scale-105 transition-transform duration-500"
                  />
                  <div className="absolute top-4 left-4 bg-red-600 text-white text-xs font-bold px-3 py-1.5 rounded-md shadow-lg uppercase tracking-wide">
                    {program.badge}
                  </div>
                </div>

                <div className="p-6">
                  <h4 className="text-xl font-bold text-white mb-1">{program.title}</h4>
                  <p className="text-sm text-amber-200 font-medium mb-3">{program.tagline}</p>
                  <p className="text-sm text-indigo-100 leading-relaxed mb-4">{program.description}</p>
                  <div className="inline-flex items-center gap-2 text-pink-300 group-hover:text-pink-200 font-semibold text-sm">
                    Register Now
                    <ArrowRight className="h-4 w-4 group-hover:translate-x-1 transition-transform" />
                  </div>
                </div>
              </a>
            ))}
          </div>

          {/* Big Registration CTA */}
          <div className="text-center">
            <div className="flex flex-col sm:flex-row gap-4 justify-center items-center">
              <a
                href={REGISTRATION_URL}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-block"
                data-testid="programs-register-btn"
              >
                <Button className="bg-gradient-to-r from-emerald-500 via-green-500 to-emerald-600 hover:from-emerald-600 hover:via-green-600 hover:to-emerald-700 text-white font-semibold py-6 px-10 text-base shadow-2xl hover:shadow-emerald-500/50 group">
                  <ClipboardEdit className="mr-2 h-5 w-5" />
                  Register for a Bootcamp
                  <ArrowRight className="ml-2 h-4 w-4 group-hover:translate-x-1 transition-transform" />
                </Button>
              </a>
              <a
                href={WHATSAPP_URL}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-block"
                data-testid="programs-whatsapp-btn"
              >
                <Button className="bg-gradient-to-r from-[#25D366] to-[#128C7E] hover:from-[#1ebe5d] hover:to-[#0f7468] text-white font-semibold py-6 px-10 text-base shadow-2xl hover:shadow-emerald-500/50">
                  <MessageCircle className="mr-2 h-5 w-5 fill-white" />
                  Chat on WhatsApp
                </Button>
              </a>
            </div>
            <p className="text-xs text-indigo-200/70 mt-3">
              Or message us directly: <a href={WHATSAPP_URL} target="_blank" rel="noopener noreferrer" className="text-emerald-300 hover:text-emerald-200 font-medium">{WHATSAPP_NUMBER_DISPLAY}</a>
            </p>
          </div>
        </div>

        {/* Book Launch Replay */}
        <Card
          className="mb-12 p-6 md:p-10 bg-gradient-to-br from-amber-500/10 via-pink-500/10 to-purple-500/10 backdrop-blur-md border border-amber-300/30 overflow-hidden"
          data-testid="book-launch-replay-card"
        >
          <div className="grid md:grid-cols-2 gap-8 items-center">
            <a
              href={YOUTUBE_REPLAY_URL}
              target="_blank"
              rel="noopener noreferrer"
              className="relative group block rounded-xl overflow-hidden shadow-2xl"
              data-testid="book-launch-poster-link"
            >
              <img
                src={BOOK_LAUNCH_POSTER}
                alt="Virtual Book Launch — May 16, 2026"
                className="w-full h-auto group-hover:scale-105 transition-transform duration-500"
              />
              <div className="absolute inset-0 bg-black/30 group-hover:bg-black/50 transition-colors flex items-center justify-center">
                <div className="h-20 w-20 md:h-24 md:w-24 rounded-full bg-red-600 group-hover:bg-red-700 flex items-center justify-center shadow-2xl group-hover:scale-110 transition-transform">
                  <Play className="h-10 w-10 md:h-12 md:w-12 text-white fill-white ml-1" />
                </div>
              </div>
              <div className="absolute top-4 right-4 bg-red-600 text-white text-xs font-bold px-3 py-1.5 rounded-md shadow-lg">
                ▶ YouTube
              </div>
            </a>

            <div>
              <div className="inline-block px-3 py-1 mb-3 rounded-full bg-amber-400/20 border border-amber-300/40 text-amber-200 text-xs font-semibold tracking-wide uppercase">
                Replay Available
              </div>
              <h3 className="text-2xl md:text-3xl font-serif font-bold text-white mb-3 leading-tight">
                Watch the Virtual Book Launch &amp; Parenting Conference
              </h3>
              <p className="text-indigo-100 leading-relaxed mb-4">
                A life-transforming parenting conference and the official launch of the <em>My Daily Affirmations</em> book series. Hear from <span className="text-white font-medium">Esther Ojosemako, Yejide Ipadeola, JayMikee, Dr. Flora O. Ayodele</span>, Evang. Temidayo Ayodele and more — moderated by Olateju Olatunji and Promise Falodun.
              </p>
              <p className="text-indigo-200/80 text-sm mb-6">
                Originally streamed live on Zoom · May 16, 2026
              </p>
              <a
                href={YOUTUBE_REPLAY_URL}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-block"
                data-testid="book-launch-watch-btn"
              >
                <Button className="bg-red-600 hover:bg-red-700 text-white font-semibold py-6 px-8 text-base shadow-lg hover:shadow-xl group">
                  <Play className="mr-2 h-4 w-4 fill-white" />
                  Watch the Replay on YouTube
                  <ArrowRight className="ml-2 h-4 w-4 group-hover:translate-x-1 transition-transform" />
                </Button>
              </a>
            </div>
          </div>
        </Card>

        {/* Monthly Learning Club */}
        <div className="mb-12" data-testid="learning-club-section">
          <div className="text-center mb-10">
            <div className="inline-flex items-center gap-2 px-4 py-1.5 mb-4 rounded-full bg-emerald-400/20 border border-emerald-300/40 text-emerald-200 text-xs font-bold tracking-widest uppercase">
              <Sparkles className="h-3 w-3" />
              Year-Round Membership
            </div>
            <h3 className="text-3xl md:text-5xl font-serif font-bold text-white mb-3">
              Monthly Learning Club
            </h3>
            <p className="text-amber-200 font-medium mb-4">For Children &amp; Teens · Ages 6 – 19</p>
            <p className="text-indigo-100 max-w-3xl mx-auto leading-relaxed">
              A year-round learning club where each month rotates through themed sections while keeping a few core subjects, so your child stays engaged, grows continuously, and joins a global community of well-rounded learners.
            </p>
          </div>

          {/* 7 Themed Sections */}
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5 mb-12">
            {LEARNING_CLUB_SECTIONS.map((section) => {
              const Icon = section.icon;

              // Branded flyer variant — display the full flyer, no redundant text
              if (section.flyer) {
                return (
                  <Card
                    key={section.title}
                    className="group relative bg-white/5 backdrop-blur-md border border-white/10 hover:bg-white/10 hover:-translate-y-1 transition-all duration-300 overflow-hidden"
                    data-testid={`learning-section-${section.title.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-|-$/g, '')}`}
                  >
                    <img
                      src={section.flyer}
                      alt={`${section.title} — Global 360° Child`}
                      className="w-full h-auto block group-hover:scale-[1.02] transition-transform duration-500"
                      loading="lazy"
                    />
                  </Card>
                );
              }

              // Fallback: AI banner + text card (for sections without a branded flyer yet)
              return (
                <Card
                  key={section.title}
                  className="group relative bg-white/5 backdrop-blur-md border border-white/10 hover:bg-white/10 hover:-translate-y-1 transition-all duration-300 overflow-hidden"
                  data-testid={`learning-section-${section.title.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-|-$/g, '')}`}
                >
                  <div className="relative h-40 overflow-hidden">
                    <img
                      src={section.image}
                      alt={section.title}
                      className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500"
                      loading="lazy"
                    />
                    <div className={`absolute inset-0 bg-gradient-to-br ${section.accent} opacity-40 mix-blend-multiply`}></div>
                    <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-black/10 to-transparent"></div>
                    <div className="absolute bottom-3 left-3 flex items-center gap-2">
                      <div className="bg-white/25 backdrop-blur-sm h-11 w-11 rounded-xl flex items-center justify-center shadow-lg">
                        <Icon className="h-6 w-6 text-white drop-shadow-md" strokeWidth={2.2} />
                      </div>
                    </div>
                  </div>

                  <div className="p-6">
                    <h4 className="text-lg font-bold text-white mb-1">{section.title}</h4>
                    <p className="text-xs text-amber-200 italic mb-3">{section.subtitle}</p>
                    <ul className="space-y-1.5">
                      {section.items.map((item) => (
                        <li key={item} className="flex items-start gap-2 text-sm text-indigo-100">
                          <span className={`mt-1 h-1.5 w-1.5 rounded-full bg-gradient-to-r ${section.accent} flex-shrink-0`}></span>
                          <span>{item}</span>
                        </li>
                      ))}
                    </ul>
                  </div>
                </Card>
              );
            })}
          </div>

          {/* Weekly Schedule + Benefits */}
          <div className="grid md:grid-cols-2 gap-6 mb-10">
            <Card className="p-6 md:p-7 bg-white/5 backdrop-blur-md border border-white/10">
              <div className="flex items-center gap-2 mb-5">
                <CalendarDays className="h-5 w-5 text-amber-300" />
                <h4 className="text-xl font-bold text-white">Weekend Class Schedule</h4>
              </div>
              <p className="text-xs text-indigo-200/80 mb-4">Each subject meets twice a week &mdash; 1 hour on Friday, 2 hours on Saturday. All times in West Africa Time (WAT).</p>

              <div className="mb-5">
                <div className="flex items-center gap-2 mb-3 pb-2 border-b border-amber-300/30">
                  <span className="text-xs font-bold text-amber-300 uppercase tracking-widest">Friday</span>
                  <span className="text-xs text-indigo-200/60">· 4:00 PM – 11:00 PM</span>
                </div>
                <div className="space-y-2">
                  {WEEKLY_SCHEDULE_FRIDAY.map((slot) => (
                    <div key={`fri-${slot.time}`} className="flex items-start gap-3 text-sm">
                      <div className="min-w-[110px] text-amber-200 font-mono text-xs">{slot.time.replace(' WAT', '')}</div>
                      <div className="text-indigo-100 flex-1">{slot.focus}</div>
                    </div>
                  ))}
                </div>
              </div>

              <div>
                <div className="flex items-center gap-2 mb-3 pb-2 border-b border-cyan-300/30">
                  <span className="text-xs font-bold text-cyan-300 uppercase tracking-widest">Saturday</span>
                  <span className="text-xs text-indigo-200/60">· 8:00 AM – 10:00 PM</span>
                </div>
                <div className="space-y-2">
                  {WEEKLY_SCHEDULE_SATURDAY.map((slot) => (
                    <div
                      key={`sat-${slot.time}`}
                      className={`flex items-start gap-3 text-sm ${slot.isBreak ? 'italic opacity-60' : ''}`}
                    >
                      <div className={`min-w-[110px] font-mono text-xs ${slot.isBreak ? 'text-indigo-200/60' : 'text-cyan-200'}`}>{slot.time.replace(' WAT', '')}</div>
                      <div className="text-indigo-100 flex-1">{slot.focus}</div>
                    </div>
                  ))}
                </div>
              </div>
            </Card>

            <Card className="p-6 md:p-7 bg-white/5 backdrop-blur-md border border-white/10">
              <div className="flex items-center gap-2 mb-5">
                <CheckCircle2 className="h-5 w-5 text-emerald-300" />
                <h4 className="text-xl font-bold text-white">Membership Benefits</h4>
              </div>
              <ul className="grid grid-cols-1 gap-2">
                {MEMBERSHIP_BENEFITS.map((benefit) => (
                  <li key={benefit} className="flex items-start gap-2 text-sm text-indigo-100">
                    <CheckCircle2 className="h-4 w-4 text-emerald-400 mt-0.5 flex-shrink-0" />
                    <span>{benefit}</span>
                  </li>
                ))}
              </ul>
            </Card>
          </div>

          {/* Pricing */}
          <div className="mb-4 text-center">
            <p className="text-sm text-amber-200 font-semibold uppercase tracking-widest">Per Child · Per Month</p>
            <p className="text-xs text-indigo-200/70 mt-1">Subscription is billed for each child enrolled</p>
          </div>
          <div className="mb-10 grid sm:grid-cols-3 gap-4 max-w-3xl mx-auto">
            {[
              { flag: '🇳🇬', label: 'Naira', price: '₦50,000', testId: 'club-price-ngn' },
              { flag: '💵', label: 'USD', price: '$50', testId: 'club-price-usd' },
              { flag: '💶', label: 'EUR', price: '€50', testId: 'club-price-eur' },
            ].map((tier) => (
              <div
                key={tier.label}
                className="bg-gradient-to-br from-amber-500/20 via-pink-500/10 to-purple-500/20 backdrop-blur-md border border-amber-300/30 rounded-2xl p-5 text-center hover:-translate-y-1 transition-transform"
                data-testid={tier.testId}
              >
                <div className="text-2xl mb-1">{tier.flag}</div>
                <div className="text-xs uppercase tracking-widest text-amber-200 font-semibold mb-1">{tier.label} · per child / month</div>
                <div className="text-3xl font-extrabold text-white">{tier.price}</div>
                <div className="text-xs text-indigo-200/70 mt-1">Cancel anytime</div>
              </div>
            ))}
          </div>

          {/* Join CTA */}
          <div className="bg-gradient-to-r from-emerald-500/20 via-teal-500/20 to-cyan-500/20 backdrop-blur-md border border-emerald-300/30 rounded-2xl p-8 md:p-10">
            <h4 className="text-2xl md:text-3xl font-serif font-bold text-white mb-2 text-center">Ready to enroll?</h4>
            <p className="text-indigo-100 mb-8 max-w-xl mx-auto text-center">
              Rolling enrollment &mdash; start any month. Choose a payment method or chat with us on WhatsApp.
            </p>

            {/* Stripe row */}
            <div className="max-w-3xl mx-auto mb-6">
              <p className="text-xs font-semibold text-amber-200 uppercase tracking-widest mb-3 text-center">Pay by Card · Apple Pay · Google Pay</p>
              <div className="grid sm:grid-cols-3 gap-3">
                <Button
                  onClick={() => createStripeCheckout('Global 360° Child Monthly Learning Club (per child)', 'subscription', 50, 'usd')}
                  className="bg-gradient-to-r from-indigo-500 to-purple-600 hover:from-indigo-600 hover:to-purple-700 text-white font-semibold py-5"
                  data-testid="learning-club-stripe-usd"
                >
                  <CreditCard className="mr-2 h-4 w-4" />
                  Pay $50 / month
                </Button>
                <Button
                  onClick={() => createStripeCheckout('Global 360° Child Monthly Learning Club (per child)', 'subscription', 50, 'eur')}
                  className="bg-gradient-to-r from-indigo-500 to-purple-600 hover:from-indigo-600 hover:to-purple-700 text-white font-semibold py-5"
                  data-testid="learning-club-stripe-eur"
                >
                  <CreditCard className="mr-2 h-4 w-4" />
                  Pay €50 / month
                </Button>
                <Button
                  onClick={() => createStripeCheckout('Global 360° Child Monthly Learning Club (per child)', 'subscription', 50000, 'ngn')}
                  className="bg-gradient-to-r from-indigo-500 to-purple-600 hover:from-indigo-600 hover:to-purple-700 text-white font-semibold py-5"
                  data-testid="learning-club-stripe-ngn"
                >
                  <CreditCard className="mr-2 h-4 w-4" />
                  Pay ₦50,000 / month
                </Button>
              </div>
            </div>

            {/* PayPal recurring */}
            <div className="max-w-3xl mx-auto mb-6">
              <p className="text-xs font-semibold text-amber-200 uppercase tracking-widest mb-3 text-center">Or Subscribe via PayPal (auto-renewing)</p>
              <a
                href="https://www.paypal.com/cgi-bin/webscr?cmd=_xclick-subscriptions&business=abiolateejae@gmail.com&item_name=Global%20360%C2%B0%20Child%20Monthly%20Learning%20Club%20(per%20child)&a3=50&p3=1&t3=M&src=1&sra=1&no_shipping=1&currency_code=USD"
                target="_blank"
                rel="noopener noreferrer"
                className="block"
                data-testid="learning-club-paypal-btn"
              >
                <Button className="w-full bg-gradient-to-r from-blue-500 to-blue-700 hover:from-blue-600 hover:to-blue-800 text-white font-semibold py-5">
                  Subscribe with PayPal &mdash; $50 / month (auto-renewing)
                </Button>
              </a>
            </div>

            {/* WhatsApp fallback */}
            <div className="max-w-3xl mx-auto text-center">
              <p className="text-xs text-indigo-200/70 mb-3">Prefer to chat before paying? Or need bank transfer / sibling discount?</p>
              <a
                href={WHATSAPP_LEARNING_CLUB_URL}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-block"
                data-testid="learning-club-whatsapp-btn"
              >
                <Button className="bg-gradient-to-r from-[#25D366] to-[#128C7E] hover:from-[#1ebe5d] hover:to-[#0f7468] text-white font-bold py-5 px-8 shadow-2xl hover:shadow-emerald-500/50">
                  <MessageCircle className="mr-2 h-5 w-5 fill-white" />
                  Chat on WhatsApp
                  <ArrowRight className="ml-2 h-4 w-4" />
                </Button>
              </a>
            </div>
            <p className="text-xs text-indigo-200/70 mt-4 text-center">
              Cancel anytime · One subscription per child
            </p>
          </div>
        </div>

        {/* Section 8: US College Admissions & Scholarship Mentorship */}
        <div className="mb-12" data-testid="us-college-mentorship-section">
          <Card className="relative overflow-hidden bg-gradient-to-br from-blue-950 via-indigo-950 to-slate-900 border border-blue-400/30 shadow-2xl">
            <div className="absolute top-0 left-0 right-0 h-1.5 bg-gradient-to-r from-red-500 via-white to-blue-500 z-10"></div>

            <div className="p-6 md:p-10">
              {/* Branded flyer */}
              <div className="max-w-4xl mx-auto mb-8 rounded-2xl overflow-hidden shadow-2xl">
                <img
                  src="https://customer-assets-gfyr7b9c.emergentagent.net/job_leadership-impact-3/artifacts/sedibr9o_ae099d97-73d5-4c37-8008-d0da1fab2a38.jpeg"
                  alt="U.S. College Admissions & Scholarship Mentorship — Global 360° Child"
                  className="w-full h-auto block"
                  loading="lazy"
                  data-testid="us-college-flyer"
                />
              </div>

              {/* Investment Highlight */}
              <div className="max-w-md mx-auto mb-8 bg-gradient-to-br from-yellow-400 to-amber-500 rounded-2xl p-6 text-center shadow-2xl">
                <p className="text-xs font-bold text-yellow-900 uppercase tracking-widest mb-1">One-Time Investment</p>
                <p className="text-5xl font-extrabold text-slate-900 mb-1">$3,000</p>
                <p className="text-sm font-semibold text-yellow-900">USD · Full Program</p>
              </div>

              {/* Compliance Note */}
              <div className="max-w-3xl mx-auto mb-8 bg-yellow-500/10 border-l-4 border-yellow-400 rounded-r-lg p-4 text-xs text-yellow-100/90 leading-relaxed">
                <strong className="text-yellow-200 block mb-1">Please note:</strong>
                This program provides expert mentorship and guidance throughout the admissions and scholarship application process. Admission and scholarship decisions are made independently by colleges, universities, and scholarship organizations and therefore cannot be guaranteed.
              </div>

              {/* CTAs */}
              <div className="flex justify-center">
                <a
                  href={WHATSAPP_MENTORSHIP_URL}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-block"
                  data-testid="mentorship-whatsapp-btn"
                >
                  <Button className="bg-gradient-to-r from-[#25D366] to-[#128C7E] hover:from-[#1ebe5d] hover:to-[#0f7468] text-white font-semibold py-6 px-8 text-base shadow-2xl hover:shadow-emerald-500/50">
                    <MessageCircle className="mr-2 h-5 w-5 fill-white" />
                    Enquire on WhatsApp
                  </Button>
                </a>
              </div>
              <p className="text-xs text-blue-200/60 mt-4 text-center">
                One-on-one · Private mentorship schedule · Rolling admissions · Not part of the weekend class timetable
              </p>
            </div>
          </Card>
        </div>

        {/* Vision + CTA */}
        <Card className="p-8 md:p-12 bg-gradient-to-br from-white/10 to-white/5 backdrop-blur-md border border-white/20">
          <div className="grid md:grid-cols-5 gap-8 items-center">
            <div className="md:col-span-3">
              <h3 className="text-2xl md:text-3xl font-serif font-bold text-white mb-4">
                Building tomorrow&rsquo;s leaders, today.
              </h3>
              <p className="text-indigo-100 leading-relaxed mb-3">
                Global 360° Child is an emerging initiative dedicated to nurturing children holistically — across faith, intellect, character, and creativity — so they thrive personally and contribute meaningfully to their communities and the world.
              </p>
              <p className="text-indigo-200/80 leading-relaxed mb-4">
                If you&rsquo;re a parent, educator, faith leader, or organization that shares this vision, I&rsquo;d love to connect.
              </p>
              <a
                href={INSTAGRAM_URL}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-2 text-pink-300 hover:text-pink-200 transition-colors font-medium group"
                data-testid="global-360-instagram-link"
              >
                <Instagram className="h-5 w-5 group-hover:scale-110 transition-transform" />
                <span>Follow @global360child on Instagram</span>
                <ArrowRight className="h-4 w-4 group-hover:translate-x-1 transition-transform" />
              </a>
            </div>
            <div className="md:col-span-2 flex flex-col gap-3">
              <Button
                onClick={() => scrollToSection('contact')}
                className="bg-gradient-to-r from-amber-400 to-pink-500 hover:from-amber-500 hover:to-pink-600 text-white font-semibold py-6 text-base shadow-lg hover:shadow-xl group"
                data-testid="global-360-partner-btn"
              >
                Partner With Us
                <ArrowRight className="ml-2 h-4 w-4 group-hover:translate-x-1 transition-transform" />
              </Button>
              <a
                href={INSTAGRAM_URL}
                target="_blank"
                rel="noopener noreferrer"
                className="block"
                data-testid="global-360-instagram-btn"
              >
                <Button className="w-full bg-gradient-to-r from-pink-500 via-fuchsia-500 to-purple-600 hover:from-pink-600 hover:via-fuchsia-600 hover:to-purple-700 text-white font-semibold py-6 text-base shadow-lg hover:shadow-xl">
                  <Instagram className="mr-2 h-4 w-4" />
                  Follow on Instagram
                </Button>
              </a>
              <Button
                onClick={() => scrollToSection('contact')}
                className="bg-white/10 hover:bg-white/20 text-white border border-white/30 backdrop-blur-sm py-6 text-base font-semibold"
                data-testid="global-360-learn-more-btn"
              >
                Get Involved
              </Button>
            </div>
          </div>
        </Card>
      </div>
    </section>
  );
};

export default Global360Child;
